import type {
  ICampaignReturnResult,
  IMaintenanceReturnProductResponse,
  IMaintenanceReturnResult,
  IResultEntity,
  IReturnType,
  ISalesReturnResult,
} from "./product-returns/IProductReturn";
import { ReturnType } from "./ReturnType";

const sumReduce = (prev: number, curr: number) => prev + curr;

export enum MaintenanceReturnStatus {
  PENDING = "PENDING",
  IN_PROCESS = "IN_PROCESS",
  FINISHED = "FINISHED",
  ON_HOLD = "ON_HOLD",
  CANCELLED = "CANCELLED",
}

export enum RepairStatusForCustomer {
  ADVANCED_REPLACEMENT_SENT_WAITING_FOR_RETURN = "ADVANCED_REPLACEMENT_SENT_WAITING_FOR_RETURN",
  CANCELLED = "CANCELLED",
  FINISHED = "FINISHED",
  PRODUCT_ARRIVED_TO_ILOQ = "PRODUCT_ARRIVED_TO_ILOQ",
  NEW_WAITING_FOR_PRODUCT = "NEW_WAITING_FOR_PRODUCT",
  NOT_WARRANTY_SCRAPPED = "NOT_WARRANTY_SCRAPPED",
  NO_FAULT_FOUND_NOT_WARRANTY_SERIALNUMBER_CHANGED = "NO_FAULT_FOUND_NOT_WARRANTY_SERIALNUMBER_CHANGED",
  NO_FAULT_FOUND_NO_WARRANTY = "NO_FAULT_FOUND_NO_WARRANTY",
  REPAIRED_NOT_WARRANTY = "REPAIRED_NOT_WARRANTY",
  REPAIRED_WARRANTY = "REPAIRED_WARRANTY",
  REPAIR_OFFER_SENT = "REPAIR_OFFER_SENT",
  REPLACED_FREE_OF_CHARGE = "REPLACED_FREE_OF_CHARGE",
  REPLACED_NOT_WARRANTY = "REPLACED_NOT_WARRANTY",
  REPLACED_WARRANTY = "REPLACED_WARRANTY",
  RETURN_ARRIVED = "RETURN_ARRIVED",
  RETURN_WITHOUT_A_REPAIR = "RETURN_WITHOUT_A_REPAIR",
  UNDEFINED = "UNDEFINED",
}

// enrich for backwards compatibility
export const enrichMaintenanceReturnData = (returns: any[]): any[] =>
  returns.map((r) => ({
    ...r,
    rmaId: r.serviceOrderNumber,
    returnType: ReturnType.MAINTENANCE_RETURN,
  }));

export const salesOrCampaignReturnQty = (
  data: ISalesReturnResult | ICampaignReturnResult,
): number =>
  ((data.productReturnData?.productDetails?.products as any[]) || [])
    .map(({ returnCount }: any) => returnCount)
    .reduce(sumReduce, 0);

export const maintenanceReturnQty = (data: IMaintenanceReturnResult): number =>
  (data.productDetails?.sites || [])
    .map(({ products }) => (products || []).map(({ instances }) => instances.length))
    .flat()
    .reduce(sumReduce, 0);

export const maintenanceReturnReplacedQty = (data: IMaintenanceReturnResult): number =>
  (data.productDetails?.sites || [])
    .map(({ products }) =>
      (products || []).map(({ instances, replacementNeeded }) =>
        replacementNeeded ? instances.length : 0,
      ),
    )
    .flat()
    .reduce(sumReduce, 0);

const maintenanceReturnProductInstanceStatuses = ({
  instances,
}: IMaintenanceReturnProductResponse): RepairStatusForCustomer[] =>
  instances.map(({ repairStatusForCustomer }) => repairStatusForCustomer);

const maintenanceReturnOrderLineStatuses = (
  data: IMaintenanceReturnResult,
): RepairStatusForCustomer[] =>
  data.productDetails.sites
    .map((site) => site.products.map(maintenanceReturnProductInstanceStatuses))
    .flat(2);

export const quotationSentForSomeLine = (data: IMaintenanceReturnResult): boolean => {
  const statuses = maintenanceReturnOrderLineStatuses(data);
  return statuses.includes(RepairStatusForCustomer.REPAIR_OFFER_SENT);
};

export const quotationSentForSomeProductLine = (
  data: IMaintenanceReturnProductResponse,
): boolean => {
  const statuses = maintenanceReturnProductInstanceStatuses(data);
  return statuses.includes(RepairStatusForCustomer.REPAIR_OFFER_SENT);
};

export const getReturnType = (
  data: IResultEntity | ISalesReturnResult | ICampaignReturnResult | IMaintenanceReturnResult,
): IReturnType | undefined => {
  if ("returnType" in data) {
    return data.returnType;
  }
  if ("productReturnData" in data) {
    return (data as IResultEntity).productReturnData?.returnType;
  }
  return undefined;
};

// Check if status exists before compare
export const isProductReturnInProgress = (status: MaintenanceReturnStatus) =>
  status && status !== MaintenanceReturnStatus.FINISHED;

// Returns are already sorted by created date.
// Reoder based on status: drafts first,  then in progress and
// last complete and returns without status
export const orderProductReturnListItems = (
  returnList: (IResultEntity | IMaintenanceReturnResult)[],
  showDrafts?: boolean,
): (IMaintenanceReturnResult | IResultEntity)[] => {
  const drafts = (showDrafts && returnList.filter(({ draft }: any) => draft)) || [];
  const created = returnList.filter(({ draft }: any) => !draft);

  const inProgress = (created as IMaintenanceReturnResult[]).filter(({ status }) =>
    isProductReturnInProgress(status),
  );

  // Include handled and those without combinedState (old returns)
  const restOfCreated = (created as IMaintenanceReturnResult[]).filter(
    ({ status }: IMaintenanceReturnResult) => !isProductReturnInProgress(status),
  );

  return [...drafts, ...inProgress, ...restOfCreated];
};

export const maintenanceReturnBase = {
  returnType: ReturnType.MAINTENANCE_RETURN,
  productDetails: {
    sites: [
      {
        $hasErrors: false,
        products: [],
      },
    ],
  },
};

export const salesReturnBase = {
  returnType: ReturnType.SALES_RETURN,
  productDetails: {
    orderNumber: "",
    orderReference: "",
    productCount: 1,
    products: [
      {
        orderCount: undefined,
        returnCount: undefined,
      },
    ],
    returnReason: "",
    compensationReference: "",
  },
  relatedOrderNumber: "",
};

export const campaignReturnBase = {
  returnType: ReturnType.CAMPAIGN_RETURN,
  productDetails: {
    orderNumber: "",
    orderReference: "",
    productCount: 1,
    products: [
      {
        orderCount: undefined,
        returnCount: undefined,
      },
    ],
    returnReason: "",
    compensationReference: "",
  },
  relatedOrderNumber: "",
};

export function isMaintenanceReturn(returnType: string | null | undefined): boolean {
  return returnType === ReturnType.MAINTENANCE_RETURN;
}

export function isSalesReturn(returnType: string | null | undefined): boolean {
  return returnType === ReturnType.SALES_RETURN;
}

export function isCampaignReturn(returnType: string | null | undefined): boolean {
  return returnType === ReturnType.CAMPAIGN_RETURN;
}

export function getMaintenanceReturnUniqueSkus(data: IMaintenanceReturnResult): string[] {
  const skus = data.productDetails.sites
    ?.map((site) => site.products?.map((product) => product.sku))
    .flat();
  return Array.from(new Set(skus));
}

// Show maximum of 8 characters from return id.
// If the SO number is longer than 8, show "more important" last three numbers
export function getCroppedReturnId(rmaId?: string): string {
  // SO numbers for maintenance returns
  if (rmaId?.startsWith("SO")) {
    if (rmaId.length <= 8) {
      return rmaId;
    }
    return `${rmaId.substring(0, 3)}..${rmaId.substring(rmaId.length - 3, rmaId.length)}`;
  }
  // Old style return ids - sales and campaign returns
  if (rmaId) {
    return rmaId.substring(0, 8);
  }
  return "";
}
